// src/pages/Public/GuestContactUs/index.js
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'context/AlertContext';

import Help from '../Dashboard/Help';
import Faq from '../../components/Faq';

import {
  sendGuestHelpMessage,
  clearGuestHelpStatus,
} from '../../redux/actions/help';

const MAX_LEN = 6000;

const isValidEmail = (email) => {
  const v = String(email || '')
    .trim()
    .toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

const GuestContactUs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const { guestLoading, guestSuccess, guestError } = useSelector(
    (state) => state.help
  );

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  // honeypot (anti-bot). Keep hidden.
  const [website, setWebsite] = useState('');

  const nameTrimmed = useMemo(() => name.trim(), [name]);
  const emailTrimmed = useMemo(() => email.trim(), [email]);
  const phoneTrimmed = useMemo(() => phone.trim(), [phone]);
  const messageTrimmed = useMemo(() => message.trim(), [message]);

  const nameOk = nameTrimmed.length >= 2;
  const emailOk = isValidEmail(emailTrimmed);
  const messageOk = messageTrimmed.length >= 10;

  const canSend =
    nameOk &&
    emailOk &&
    messageOk &&
    messageTrimmed.length <= MAX_LEN &&
    !guestLoading;

  const onSend = () => {
    if (!canSend) return;

    dispatch(
      sendGuestHelpMessage({
        name: nameTrimmed,
        email: emailTrimmed,
        phone: phoneTrimmed || null,
        message: messageTrimmed,
        website, // honeypot (should be empty)
      })
    );
  };

  useEffect(() => {
    if (!guestSuccess) return;

    showAlert({
      type: 'success',
      title: t("contact_us.send_success_title"),
      message: t("contact_us.send_success_message"),
      primaryAction: t("contact_us.send_success_primary_action"),
    });

    setName('');
    setEmail('');
    setPhone('');
    setMessage('');
    setWebsite('');
    dispatch(clearGuestHelpStatus());
  }, [guestSuccess, showAlert, dispatch, t]);

  useEffect(() => {
    if (!guestError) return;

    showAlert({
      title: t("contact_us.send_error_title"),
      message: guestError || t("contact_us.send_error_message"),
      primaryAction: t("contact_us.send_error_primary_action"),
    });

    dispatch(clearGuestHelpStatus());
  }, [guestError, showAlert, dispatch, t]);

  useEffect(() => {
    return () => {
      dispatch(clearGuestHelpStatus());
    };
  }, [dispatch]);

  const helperText = useMemo(() => {
    if (!nameTrimmed) return t("contact_us.name_required");
    if (!nameOk) return t("contact_us.name_too_short");
    if (!emailTrimmed) return t("contact_us.email_required");
    if (!emailOk) return t("contact_us.email_invalid");
    if (messageTrimmed.length < 10) return t("contact_us.message_too_short");
    if (messageTrimmed.length > MAX_LEN) return t("contact_us.message_too_long");
    return '\u00A0';
  }, [t, nameTrimmed, nameOk, emailTrimmed, emailOk, messageTrimmed]);

  const inputBase =
    'w-full rounded-xl px-4 py-3 text-sm sm:text-base outline-none transition ' +
    'bg-gray-50 border border-gray-200 ' +
    'focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 ' +
    'placeholder-gray-400';

  const labelBase = 'text-sm font-semibold text-gray-700';

  return (
    <section className="w-full bg-gradient-to-b from-white to-gray-50">
      {/* Centered, capped width container */}
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Modern card */}
        <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="relative px-5 sm:px-8 lg:px-10 py-8 sm:py-10">
            {/* Title */}
            <div className="flex flex-col items-center text-center gap-3">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                <span className="text-yellow-500">
                  {t("dashboard.talk_to_us")}
                </span>
              </h2>

              <p className="text-sm sm:text-base text-gray-500 max-w-2xl">
                {t("contact_us.description")}
              </p>
            </div>

            {/* Form */}
            <div className="mt-8 sm:mt-10 space-y-6">
              {/* Name + Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={labelBase}>
                    {t("contact_us.name_label")}{' '}
                    <span className="text-yellow-600">*</span>
                  </label>
                  <input
                    className={inputBase}
                    placeholder={t("contact_us.name_placeholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    aria-invalid={!nameOk && !!nameTrimmed}
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelBase}>
                    {t("contact_us.email_label")}{' '}
                    <span className="text-yellow-600">*</span>
                  </label>
                  <input
                    className={inputBase}
                    placeholder={t("contact_us.email_placeholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    inputMode="email"
                    aria-invalid={!emailOk && !!emailTrimmed}
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className={labelBase}>
                  {t("contact_us.phone_label")}
                </label>
                <input
                  className={inputBase}
                  placeholder={t("contact_us.phone_placeholder")}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className={labelBase}>
                  {t("contact_us.message_label")}{' '}
                  <span className="text-yellow-600">*</span>
                </label>

                <textarea
                  className={
                    inputBase +
                    ' min-h-[160px] sm:min-h-[200px] md:min-h-[240px] resize-none p-4'
                  }
                  placeholder={t("contact_us.message_placeholder")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />

                <div className="flex items-start justify-between gap-3 text-xs sm:text-sm">
                  <p className="text-gray-500 min-h-[20px]">{helperText}</p>
                  <p className="text-gray-400 whitespace-nowrap">
                    {messageTrimmed.length}/{MAX_LEN}
                  </p>
                </div>
              </div>

              {/* Honeypot (hidden) */}
              <input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {/* Actions */}
              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={onSend}
                  disabled={!canSend}
                  className={[
                    'w-full sm:w-auto min-w-[140px] px-6 py-3 rounded-xl font-semibold transition',
                    canSend
                      ? 'bg-yellow-500 text-white hover:brightness-95 shadow-sm'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                  ].join(' ')}
                >
                  {guestLoading
                    ? t("contact_us.sending")
                    : t("contact_us.send")}
                </button>
              </div>

              {/* bottom hint */}
              <p className="text-xs text-gray-400 text-center">
                {t("contact_us.privacy_hint")}
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <Faq className="mt-6 sm:mt-8" />
      </div>
    </section>
  );
};

const ContactUs = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (!isAuthenticated) return <GuestContactUs />;

  return (
    <section className="w-full bg-gradient-to-b from-white to-gray-50">
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Help />
      </div>
    </section>
  );
};

export default ContactUs;
